/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Controller, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import {FiAlertTriangle, FiFileText, FiPaperclip} from 'react-icons/fi'
import axios from 'axios'
import {
  VmoAttachment,
  VmoButton,
  VmoCheckbox,
  VmoDropdown,
  VmoDropdownDivider,
  VmoDropdownHeader,
  VmoDropdownItem,
  VmoDropdownMenu,
  VmoEditorSmall,
  VmoForm,
  VmoFormField,
  VmoFormGroup,
  VmoFormSelect,
  VmoIcon,
  VmoInput,
  VmoModal,
  VmoModalActions,
  VmoModalContent,
  VmoModalHeader,
  vmoToast,
  VmoTooltip,
} from 'vmo-library'

import {uploadAttachments} from 'actions/attachments'
import {getAllContactsIds} from 'actions/contacts'
import {createTicket, getAllMailInbox, GetSearchedArticles, singleUserTemplates} from 'actions/inbox'
import {
  CREATE_TICKET,
  GET_ALL_CONTACTS_IDS,
  GET_MAIL_BOX,
  GET_SEARCHED_ARTICLES,
  GET_SINGLE_TEMPLATE,
} from 'actions/types'
import {getMetadataInfo} from 'utils/local-storage'
import {setMetaData} from 'actions'
import {extractTextContentFromHTML, get, removeDoubleQuotes} from 'utils/helper'
import {createTicketSchema} from 'validation/ticket.schema'
import {INBOX_TYPES} from 'constants/enums'
import useApiResponse from 'hooks/impure/useApiResponse'
import {useDebounce} from 'hooks/pure/useDebounce'
import AttachmentPreview from './AttachmentPreview'
import VmoCustomDropdown from './VmoCustomDropdown'
import {Input} from './Form'

const {CancelToken} = axios
const requestToken = CancelToken.source()

const TicketModel = props => {
  const dispatch = useDispatch()

  const ref = useRef()

  const {ticketModal, setTicketModal} = props
  const {
    handleSubmit,
    formState: {errors},
    control,
    watch,
    setValue,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    resolver: joiResolver(createTicketSchema),
    defaultValues: {
      assignee: '',
      mailbox: '',
      subject: '',
      email: '',
      name: '',
    },
  })

  const watchMailbox = watch('mailbox')

  // useState hooks
  const [listOfMail, setListOfMail] = useState([])
  const [teammates, setTeammates] = useState([])
  const [files, setFiles] = useState([])
  const [listOfContacts, setListOfContacts] = useState([])
  const [articleText, setArticleText] = useState('')
  const [article, setArticle] = useState('')
  const [sendToUser, setSendToUser] = useState(true)

  const debouncedArticleText = useDebounce(articleText, 1000)

  const [emailEditorValue, setEmailEditorValue] = useState('')
  const [emailEditorCurrentValue, setEmailEditorCurrentValue] = useState('')
  const [defaultFontSize, setDefaultFontSize] = useState(16)
  const [defaultFontFamily, setDefaultFontFamily] = useState('Roboto')
  const [uploadedFiles, setUploadedFiles] = useState([])

  const {inbox: mailboxes = [], getSingleInbox: inboxDetails = []} = useSelector(state => state.mailInbox)
  const {singleResponse} = useSelector(state => state.settings)
  const {type, isLoadingData, successLabels = []} = useSelector(state => state.apiReducer)
  const {getUserTemplates: userTemplates = [], searchedArticles = []} = useSelector(state => state.emailInbox)

  const {data: contactsIdsList = []} = useApiResponse({
    action: getAllContactsIds,
    enabled: true,
    label: GET_ALL_CONTACTS_IDS,
    storePath: 'contacts.contactsIdsList',
    onSuccess: contactsIdsList => {
      const list = contactsIdsList?.map(contact => ({
        key: contact.id,
        value: get(['email'], contact, ''),
        text: get(['email'], contact, ''),
        content: (
          <div>
            {get(['name'], contact, '')}
            <small className="ml-2 vmo-lead">({get(['email'], contact, '')})</small>
          </div>
        ),
      }))
      setListOfContacts(list || [])
    },
  })

  const templateList = useMemo(() => {
    return (
      userTemplates?.map(({id, subject}) => ({
        key: id,
        value: id,
        text: subject,
      })) ?? []
    )
  }, [userTemplates])

  useEffect(() => {
    if (debouncedArticleText.trim() !== '') {
      dispatch(GetSearchedArticles(debouncedArticleText))
    }
  }, [debouncedArticleText, dispatch])

  useEffect(() => {
    if (successLabels.includes(GET_SEARCHED_ARTICLES)) {
      const articles = searchedArticles?.map(obj => ({...obj, text: obj.name}))
      setArticle(articles || [])
    }
  }, [successLabels, searchedArticles])

  const onCloseModal = () => {
    setTicketModal(!ticketModal)
    setFiles([])
  }

  const onCreateTicket = formData => {
    const messageText = extractTextContentFromHTML(emailEditorCurrentValue)

    if (!messageText) {
      vmoToast.error(
        <div className="toast-body">
          <FiAlertTriangle />
          <span>Please write message</span>
        </div>
      )
      return
    }

    const data = {...formData}
    const payload = {
      to: [{email: data.email, name: data.name}],
      subject: data.subject,
      messageHtml: emailEditorCurrentValue,
      messageText,
      mailboxId: data.mailbox,
      sendToUser,
    }
    if (data.assignee !== 'unassign') {
      payload.assigneeId = data.assignee
    }
    if (files.length > 0) {
      payload.attachments = files.map(file => file.id)
    }
    dispatch(createTicket(payload))
  }

  // useEffect hooks

  useEffect(() => {
    if (successLabels.includes(GET_SINGLE_TEMPLATE)) {
      if (singleResponse) {
        setValue('subject', singleResponse.subject)
        setEmailEditorValue(singleResponse.body)
      }
    }
  }, [successLabels])

  /// Setting the teammates which are assigned to particular inbox
  useEffect(() => {
    const matchedMailBox = mailboxes.find(mailBox => {
      return watchMailbox === mailBox.id
    })

    if (matchedMailBox) setTeammates(matchedMailBox?.assignees || [])
  }, [watchMailbox])

  useEffect(() => {
    dispatch(getAllMailInbox())
  }, [dispatch])

  useEffect(() => {
    if (successLabels.includes(CREATE_TICKET)) {
      onCloseModal()

      if (!get(['ticketStatus'], getMetadataInfo())) {
        const data = {
          ticketStatus: true,
        }
        dispatch(setMetaData(data))
      }
    }
  }, [successLabels])
  useEffect(() => {
    if (mailboxes.length > 0) {
      const mailBoxList = mailboxes
        .filter(mail => (mail.type === INBOX_TYPES.EMAIL ? mail.emailVerified : true))
        .map(mail => ({
          value: mail.id,
          key: mail.id,
          content: (
            <div>
              {mail.name || ''}
              <small className="ml-2 vmo-lead">{mail.email || ''}</small>
            </div>
          ),
          text: mail.name,
        }))

      setListOfMail(mailBoxList)
    }
  }, [mailboxes])

  useEffect(() => {
    if (successLabels.includes(GET_MAIL_BOX)) {
      // Setting Signature in Compose Email Editor
      if (get(['settings', 'signatureEnabled'], inboxDetails, false) === true && inboxDetails.signature !== {}) {
        const editedSignature = '<br><br><br>'.concat(inboxDetails.signature.body)
        setEmailEditorValue(editedSignature)
      } else {
        setEmailEditorValue('')
      }

      // Checking if setting object is empty or not
      if (Object.keys(get(['settings'], inboxDetails, [])).length !== 0) {
        /// Setting Font of Inbox Setting

        if (inboxDetails.settings.fontSize !== null) {
          setDefaultFontSize(inboxDetails.settings.fontSize)
        }

        /// Setting Font of Inbox Setting
        if (inboxDetails.settings.fontFamily !== null) {
          setDefaultFontFamily(inboxDetails.settings.fontFamily)
        }
      } else {
        setDefaultFontSize(16)
        setDefaultFontFamily('Impact')
      }
    }
  }, [successLabels])

  return (
    <VmoModal open={ticketModal} onClose={onCloseModal} closeIcon size="small" centered={false}>
      <VmoModalHeader>Create Ticket</VmoModalHeader>
      <VmoModalContent scrolling>
        <VmoForm className="errorLabel" onSubmit={handleSubmit(onCreateTicket)} ref={ref}>
          <VmoFormGroup widths="equal">
            <VmoFormField required>
              <label>Email</label>
              <Controller
                name="email"
                control={control}
                render={({field: {value, onChange, onBlur}}) => (
                  <VmoFormSelect
                    options={listOfContacts}
                    placeholder="Type your email.."
                    value={value}
                    onBlur={onBlur}
                    onChange={(e, {value}) => {
                      onChange(value)
                      const name = contactsIdsList.find(con => con.email === value)
                      if (name) {
                        setValue('name', `${get(['name'], name, ' ')}`, {shouldValidate: true})
                      } else {
                        setValue('name', '')
                      }
                    }}
                    fluid
                    search
                    allowAdditions
                    onAddItem={(e, {value}) => {
                      setListOfContacts([{text: value, value}, ...listOfContacts])
                    }}
                    error={
                      errors.email && {
                        content: removeDoubleQuotes(errors.email.message),
                      }
                    }
                  />
                )}
              />
            </VmoFormField>
            <Input
              name="name"
              control={control}
              label="Name"
              placeholder="Type name.."
              maxLength={20}
              type="text"
              required
            />
          </VmoFormGroup>
          <Input
            name="subject"
            control={control}
            type="text"
            label="Subject"
            placeholder="Type your Subject.."
            maxLength={20}
            required
          />

          <VmoFormGroup widths="equal">
            <VmoFormField required>
              <label>Inbox</label>
              <Controller
                name="mailbox"
                control={control}
                render={({field: {value, onChange, onBlur}}) => (
                  <VmoFormSelect
                    fluid
                    selection
                    options={listOfMail}
                    value={value}
                    onBlur={onBlur}
                    onChange={(e, {value}) => {
                      onChange(value)
                      // setValue('assignee', null, {
                      //   shouldValidate: true,
                      // })
                    }}
                    error={
                      errors.mailbox && {
                        content: removeDoubleQuotes(errors.mailbox.message),
                      }
                    }
                  />
                )}
              />
            </VmoFormField>
            <VmoFormField required>
              <label>Assignee</label>
              <Controller
                name="assignee"
                control={control}
                render={({field: {value, onChange, onBlur}}) => (
                  <VmoFormSelect
                    fluid
                    selection
                    options={[...teammates, {id: 'unassign', firstName: 'Unassign'}].map(teammate => {
                      return {
                        value: teammate.id,
                        key: teammate.id,
                        text: teammate.firstName,
                        content: (
                          <div>
                            {`${teammate.firstName || ''} ${teammate.lastName || ''}`.trim() || '-'}
                            <small className="ml-2 vmo-lead">{teammate.email ? `(${teammate.email})` : ''}</small>
                          </div>
                        ),
                      }
                    })}
                    value={value}
                    onBlur={onBlur}
                    onChange={(e, {value}) => {
                      onChange(value)
                    }}
                    error={
                      errors.assignee && {
                        content: removeDoubleQuotes(errors.assignee.message),
                      }
                    }
                  />
                )}
              />
            </VmoFormField>
          </VmoFormGroup>

          <VmoFormField className="msgEditor" required>
            <label>Message</label>
            <VmoEditorSmall
              setContents={emailEditorValue}
              onChange={content => {
                setEmailEditorCurrentValue(content)
              }}
              setDefaultStyle={`font-size: ${defaultFontSize}px; font-family: ${defaultFontFamily};`}
            />
          </VmoFormField>
          <VmoTooltip
            content="Attachment"
            trigger={
              <VmoAttachment
                content={<FiPaperclip />}
                multiple
                onChange={e => {
                  const data = {
                    file: e,
                  }
                  if (e.length > 0) {
                    dispatch(uploadAttachments(data, 'ticketAttachment', requestToken))
                  }
                }}
                onClick={e => {
                  e.target.value = null
                }}
                fileList={files => {
                  setFiles(files)
                }}
              />
            }
          />
          <AttachmentPreview
            success="ticketAttachment"
            files={files}
            setFiles={setFiles}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
        </VmoForm>
      </VmoModalContent>
      <VmoModalActions className="flex flex-justify-end flex-items-center" style={{gap: 'var(--gap-medium)'}}>
        <VmoCheckbox label="Send To User" checked={sendToUser} onClick={() => setSendToUser(!sendToUser)} />

        <VmoCustomDropdown
          options={templateList}
          multiple
          dropdownProps={{
            button: true,
            upward: true,
            className: 'icon',
            icon: (
              <VmoTooltip
                size="small"
                content="Email templates"
                trigger={
                  <VmoButton icon>
                    <VmoIcon className="list" />
                  </VmoButton>
                }
              />
            ),
          }}
          header="Insert Email Templates"
          menu={{scrolling: true}}
          input={{
            icon: 'search',
            iconPosition: 'left',
            className: 'search',
            placeholder: 'Search email templates... ',
          }}
          onClick={value => {
            dispatch(singleUserTemplates(value, null, 'templateListCompose'))
          }}
        />

        <VmoDropdown
          icon={
            <VmoTooltip
              size="tiny"
              content="Insert Article"
              trigger={
                <VmoButton icon>
                  <FiFileText id="TooltipTagInbox" />
                </VmoButton>
              }
            />
          }
          button
          upward
          className="icon"
          onClick={() => setArticleText('')}
          closeOnChange={false}
        >
          <VmoDropdownMenu>
            <VmoDropdownHeader>Insert Articles</VmoDropdownHeader>
            <VmoInput
              icon="search"
              iconPosition="left"
              placeholder="Search to get results"
              className="search"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onChange={e => {
                setArticleText(e.target.value)
                if (e.target.value.trim() === '') setArticle('')
              }}
            />
            {articleText.length > 0 && <VmoDropdownDivider />}

            {article && article[0]
              ? article.map(option => {
                  return (
                    <VmoDropdownItem
                      key={option.text}
                      {...option}
                      onClick={() => {
                        setEmailEditorValue(
                          `${emailEditorCurrentValue}<a href=${option.webLink} target="_blank">${option.webLink}</a>`
                        )
                      }}
                    />
                  )
                })
              : articleText.length > 0 && <VmoDropdownItem disabled>No Results Found</VmoDropdownItem>}
          </VmoDropdownMenu>
        </VmoDropdown>

        <VmoButton
          className="ml-0"
          disabled={isLoadingData && type.includes(CREATE_TICKET)}
          basic
          onClick={() => onCloseModal()}
        >
          Cancel
        </VmoButton>
        <VmoButton
          className="ml-0"
          loading={isLoadingData && type.includes(CREATE_TICKET)}
          disabled={isLoadingData && type.includes(CREATE_TICKET)}
          primary
          onClick={() => ref.current.handleSubmit()}
        >
          Create Ticket
        </VmoButton>
      </VmoModalActions>
    </VmoModal>
  )
}
export default TicketModel
